// Verse Selector UI Component
import { getBooksList, getVerse, getVerses, loadBook } from '../bible.js';

/**
 * Create and show verse selector modal
 */
export async function showVerseSelector(callback) {
    // Remove existing modal if any
    const existing = document.getElementById('verse-selector-modal');
    if (existing) {
        existing.remove();
    }

    // Create modal
    const modal = document.createElement('div');
    modal.id = 'verse-selector-modal';
    modal.className = 'modal';

    modal.innerHTML = `
    <div class="modal-content verse-selector-content">
      <div class="modal-header">
        <h3 id="selector-title">📖 Sélectionner un livre</h3>
        <button class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body" id="selector-body">
        <!-- Content will be injected dynamically -->
      </div>

      <div class="modal-footer">
        <button id="cancel-verse-btn" class="btn btn-secondary">Annuler</button>
        <button id="confirm-verse-btn" class="btn btn-primary" style="display: none;">
          ✓ Utiliser ce verset
        </button>
      </div>
    </div>
  `;

    document.body.appendChild(modal);

    // Initialize logic
    await initVerseSelector(modal, callback);

    // Show modal
    modal.style.display = 'flex';
}

/**
 * Initialize verse selector
 */
async function initVerseSelector(modal, callback) {
    const title = modal.querySelector('#selector-title');
    const body = modal.querySelector('#selector-body');
    const confirmBtn = modal.querySelector('#confirm-verse-btn');
    const cancelBtn = modal.querySelector('#cancel-verse-btn');
    const closeBtn = modal.querySelector('.close-btn');

    let currentStep = 'books'; // 'books', 'chapters', 'verses'
    let selectedBook = null;
    let selectedChapter = null;
    let selectedVerseStart = null;
    let selectedVerseEnd = null;
    let version = localStorage.getItem('user_preferred_lang') || 'french';

    // OT/NT filtering
    const OT_BOOKS = ['Genèse', 'Exode', 'Lévitique', 'Nombres', 'Deutéronome', 'Josué', 'Juges', 'Ruth', '1 Samuel', '2 Samuel', '1 Rois', '2 Rois', '1 Chroniques', '2 Chroniques', 'Esdras', 'Néhémie', 'Esther', 'Job', 'Psaumes', 'Proverbes', 'Ecclésiaste', 'Cantique des Cantiques', 'Ésaïe', 'Jérémie', 'Lamentations', 'Ézéchiel', 'Daniel', 'Osée', 'Joël', 'Amos', 'Abdias', 'Jonas', 'Michée', 'Nahum', 'Habacuc', 'Sophonie', 'Aggée', 'Zacharie', 'Malachie'];

    const renderBooks = async (filter = 'all') => {
        currentStep = 'books';
        title.textContent = '📖 Sélectionner un livre';
        confirmBtn.style.display = 'none';

        body.innerHTML = `
            <div class="selection-step">
                <div class="verse-tabs">
                    <button class="tab-btn ${filter === 'all' ? 'active' : ''}" data-filter="all">Tous</button>
                    <button class="tab-btn ${filter === 'ot' ? 'active' : ''}" data-filter="ot">Ancien Testament</button>
                    <button class="tab-btn ${filter === 'nt' ? 'active' : ''}" data-filter="nt">Nouveau Testament</button>
                </div>
                <div class="verse-grid" id="books-grid">
                    <div class="loading">⏳ Chargement des livres...</div>
                </div>
            </div>
        `;

        const booksGrid = body.querySelector('#books-grid');
        const tabs = body.querySelectorAll('.tab-btn');

        tabs.forEach(tab => {
            tab.addEventListener('click', () => renderBooks(tab.dataset.filter));
        });

        try {
            const books = await getBooksList(version);
            let filteredBooks = books;

            if (filter === 'ot') {
                filteredBooks = books.filter(b => OT_BOOKS.includes(b.key));
            } else if (filter === 'nt') {
                filteredBooks = books.filter(b => !OT_BOOKS.includes(b.key));
            }

            booksGrid.innerHTML = '';
            filteredBooks.forEach(book => {
                const btn = document.createElement('button');
                btn.className = 'grid-btn';
                btn.textContent = book.name;
                btn.addEventListener('click', () => {
                    selectedBook = book;
                    renderChapters();
                });
                booksGrid.appendChild(btn);
            });
        } catch (error) {
            booksGrid.innerHTML = '<div class="error">Erreur de chargement des livres</div>';
        }
    };

    const renderChapters = () => {
        currentStep = 'chapters';
        title.textContent = `📖 Sélectionner un chapitre - ${selectedBook.name}`;
        confirmBtn.style.display = 'none';

        body.innerHTML = `
            <div class="selection-step">
                <div class="step-header">
                    <button class="back-btn" id="back-to-books">←</button>
                    <span>Choisir le chapitre</span>
                </div>
                <div class="verse-grid chapters-grid" id="chapters-grid"></div>
            </div>
        `;

        body.querySelector('#back-to-books').addEventListener('click', () => renderBooks());

        const chaptersGrid = body.querySelector('#chapters-grid');
        for (let i = 1; i <= selectedBook.chapters; i++) {
            const btn = document.createElement('button');
            btn.className = 'grid-btn';
            btn.textContent = i;
            btn.addEventListener('click', () => {
                selectedChapter = i;
                renderVerses();
            });
            chaptersGrid.appendChild(btn);
        }
    };

    const renderVerses = async () => {
        currentStep = 'verses';
        title.textContent = `📖 Sélectionner les versets - ${selectedBook.name} ${selectedChapter}`;
        confirmBtn.style.display = 'block';
        confirmBtn.disabled = true;

        body.innerHTML = `
            <div class="selection-step">
                <div class="step-header">
                    <button class="back-btn" id="back-to-chapters">←</button>
                    <span>Choisir le(s) verset(s)</span>
                </div>
                <div class="verse-selection-container">
                    <div class="verse-range-selector">
                        <div class="verse-range-label">Verset de début</div>
                        <div class="verse-grid verses-grid" id="start-verses-grid">
                            <div class="loading">⏳ Chargement...</div>
                        </div>
                    </div>
                    <div class="verse-range-selector" id="end-verse-section" style="display: none;">
                        <div class="verse-range-label">Verset de fin (optionnel)</div>
                        <div class="verse-grid verses-grid" id="end-verses-grid"></div>
                    </div>
                </div>
                <div id="verse-preview-section" class="verse-preview-section" style="display: none;">
                    <h4>Aperçu</h4>
                    <div id="verse-preview-content" class="verse-preview-content"></div>
                </div>
            </div>
        `;

        body.querySelector('#back-to-chapters').addEventListener('click', () => renderChapters());

        const startGrid = body.querySelector('#start-verses-grid');
        const endGrid = body.querySelector('#end-verses-grid');
        const endSection = body.querySelector('#end-verse-section');
        const previewSection = body.querySelector('#verse-preview-section');
        const previewContent = body.querySelector('#verse-preview-content');

        try {
            // Get real verse count for the chapter using the centralized loadBook
            const bookData = await loadBook(selectedBook.key, version);
            const chapterData = bookData[selectedChapter];
            const maxVerses = chapterData ? Object.keys(chapterData).length : 150;
            
            startGrid.innerHTML = '';
            for (let i = 1; i <= maxVerses; i++) {
                const btn = document.createElement('button');
                btn.className = 'grid-btn';
                btn.textContent = i;
                btn.addEventListener('click', () => {
                    selectedVerseStart = i;
                    selectedVerseEnd = i;
                    
                    // Update UI
                    startGrid.querySelectorAll('.grid-btn').forEach(b => b.classList.remove('selected'));
                    btn.classList.add('selected');
                    
                    // Show end selection
                    renderEndVerses(i, maxVerses);
                    updatePreview();
                });
                startGrid.appendChild(btn);
            }
        } catch (error) {
            startGrid.innerHTML = '<div class="error">Erreur</div>';
        }

        const renderEndVerses = (start, max) => {
            endSection.style.display = 'block';
            endGrid.innerHTML = '';
            
            // Add a "None" or "Same" option implicitly by clicking the start verse again or selecting end=start
            for (let i = start; i <= max; i++) {
                const btn = document.createElement('button');
                btn.className = 'grid-btn';
                if (i === start) btn.classList.add('selected');
                btn.textContent = i;
                btn.addEventListener('click', () => {
                    selectedVerseEnd = i;
                    endGrid.querySelectorAll('.grid-btn').forEach(b => b.classList.remove('selected'));
                    btn.classList.add('selected');
                    updatePreview();
                });
                endGrid.appendChild(btn);
            }
        };

        const updatePreview = async () => {
            if (!selectedBook || !selectedChapter || !selectedVerseStart) return;

            previewSection.style.display = 'block';
            previewContent.innerHTML = '<div class="loading">⏳ Chargement de l\'aperçu...</div>';
            confirmBtn.disabled = true;

            try {
                const verseEnd = selectedVerseEnd || selectedVerseStart;
                const verseRef = selectedVerseStart === verseEnd 
                    ? `${selectedBook.name} ${selectedChapter}:${selectedVerseStart}`
                    : `${selectedBook.name} ${selectedChapter}:${selectedVerseStart}-${verseEnd}`;

                let verseText = '';
                if (selectedVerseStart === verseEnd) {
                    verseText = await getVerse(version, selectedBook.key, selectedChapter, selectedVerseStart);
                } else {
                    const verses = await getVerses(version, selectedBook.key, selectedChapter, selectedVerseStart, verseEnd);
                    verseText = verses.map(v => `<span class="verse-num">${v.verse}</span> ${v.text}`).join(' ');
                }

                previewContent.innerHTML = `
                    <div class="verse-ref"><strong>📖 ${verseRef}</strong></div>
                    <div class="verse-text">${verseText}</div>
                `;
                confirmBtn.disabled = false;
            } catch (error) {
                previewContent.innerHTML = '<div class="error">Erreur lors du chargement de l\'aperçu.</div>';
            }
        };
    };

    // Confirm button
    confirmBtn.addEventListener('click', () => {
        if (selectedBook && selectedChapter && selectedVerseStart && callback) {
            callback({
                book: selectedBook.key,
                bookName: selectedBook.name,
                chapter: selectedChapter,
                verseStart: selectedVerseStart,
                verseEnd: selectedVerseEnd || selectedVerseStart
            });
            modal.remove();
        }
    });

    // Close/Cancel
    const closeModal = () => modal.remove();
    cancelBtn.addEventListener('click', closeModal);
    closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });

    // Start with books
    await renderBooks();
}
